import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_MY_NOTIFICATIONS } from '../graphql/queries/myNotifications';
import { MARK_NOTIFICATION_READ } from '../graphql/mutations/markNotificationRead';
import { MARK_ALL_NOTIFICATIONS_READ } from '../graphql/mutations/markAllNotificationsRead';
import Button from '../components/ui/Button';

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const ITEMS_PER_PAGE = 10;

  const { data, loading, error, refetch } = useQuery(GET_MY_NOTIFICATIONS, {
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
      unreadOnly: unreadOnly ? true : undefined,
    },
    fetchPolicy: 'network-only',
  });

  const [markNotificationRead, { loading: markSingleLoading }] = useMutation(MARK_NOTIFICATION_READ);
  const [markAllNotificationsRead, { loading: markAllLoading }] = useMutation(MARK_ALL_NOTIFICATIONS_READ);

  const notifications = data?.currentUser?.domoreNotifications?.items || [];
  const totalCount = data?.currentUser?.domoreNotifications?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const hasUnread = notifications.some((notification: any) => !notification.read);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleMarkRead = async (id: string) => {
    setMarkingId(id);
    try {
      await markNotificationRead({ variables: { id } });
      await refetch();
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      await refetch();
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center text-green-600 mb-4 hover:text-green-700">
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Tilbage til dashboard</span>
        </button>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center">
            <Bell className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Notifikationer</h1>
              <p className="text-gray-600">Seneste aktivitet og opdateringer</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setUnreadOnly(false);
                setCurrentPage(1);
              }}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                !unreadOnly ? 'bg-green-100 text-green-800' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Alle
            </button>
            <button
              onClick={() => {
                setUnreadOnly(true);
                setCurrentPage(1);
              }}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                unreadOnly ? 'bg-green-100 text-green-800' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Ulæste
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllRead}
              disabled={!hasUnread || markAllLoading || markSingleLoading}
            >
              Marker alle som læst
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">Der opstod en fejl ved indlæsning af notifikationer.</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Ingen notifikationer</h3>
            <p className="mt-1 text-sm text-gray-500">
              {unreadOnly ? 'Du har ingen ulæste notifikationer.' : 'Du har endnu ingen notifikationer.'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {notifications.map((notification: any) => (
                <div
                  key={notification.id}
                  className={`rounded-lg border p-4 ${notification.read ? 'border-gray-200 bg-white' : 'border-green-200 bg-green-50'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex-shrink-0">
                      <Bell className={`h-5 w-5 ${notification.read ? 'text-gray-400' : 'text-green-600'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${notification.read ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
                        {notification.presentationText || 'Ny notifikation'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDateTime(notification.notificationDatetime)}
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {!notification.read && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkRead(notification.id)}
                            isLoading={markingId === notification.id && markSingleLoading}
                            disabled={markAllLoading}
                          >
                            Marker som læst
                          </Button>
                        )}
                        {notification.url && (
                          <a
                            href={notification.url}
                            className="inline-flex items-center text-sm text-green-700 hover:text-green-800"
                          >
                            Åbn <ExternalLink className="h-4 w-4 ml-1" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Viser {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} af {totalCount}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Side {currentPage} af {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-6">
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          Tilbage til dashboard
        </Button>
      </div>
    </div>
  );
};

export default Notifications;
